import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import NewsTicker from '../NewsTicker'
import MarketClock from '../MarketClock'
import ErrorBoundary from '../ErrorBoundary'
import { useAuthStore } from '../../hooks/useAuth'
import { api } from '../../lib/api'
import { NAV_ICONS } from '../../lib/navIcons'

const NAV = [
  { to: '/dashboard',    iconKey: 'dashboard',    label: 'Dashboard',    mobileLabel: 'Home' },
  { to: '/briefing',     iconKey: 'notes',        label: 'Briefing',     mobileLabel: 'Brief' },
  { to: '/analyze',      iconKey: 'analyze',      label: 'Analyze',      mobileLabel: 'Analyze' },
  { to: '/trading',      iconKey: 'trading',      label: 'Trading',      mobileLabel: 'Trade' },
  { to: '/positions',    iconKey: 'trading2',     label: 'Positions',    mobileLabel: 'Positions' },
  { to: '/options',      iconKey: 'options',       label: 'Options',      mobileLabel: 'Options' },
  { to: '/trade-log',    iconKey: 'journal',      label: 'Trade Log',    mobileLabel: 'Log' },
  { to: '/watchlist',    iconKey: 'watchlist',     label: 'Watchlist',    mobileLabel: 'Watch' },
  { to: '/scanner',      iconKey: 'scanner',      label: 'Scanner',      mobileLabel: 'Scan' },
  { to: '/intelligence', iconKey: 'intel',         label: 'Intel',        mobileLabel: 'Intel' },
  { to: '/djt',          iconKey: 'notes',         label: 'DJT Intel',    mobileLabel: 'DJT' },
  { to: '/dca',          iconKey: 'dca_calc',      label: 'Time Capsule', mobileLabel: 'DCA' },
  { to: '/settings',     iconKey: 'settings',      label: 'Settings',     mobileLabel: 'Settings' },
]

export default function Layout() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()

  // Unread alert notifications badge — Super only, polls every 60s
  const { data: notifData } = useQuery({
    queryKey: ['alert-notifs-badge'],
    queryFn:  api.alerts.notifications,
    enabled:  user?.tier === 'super',
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const unreadAlerts = notifData?.notifications?.filter((n: any) => !n.read)?.length ?? 0

  // Broadcast announcement banner
  const { data: broadcastData } = useQuery({
    queryKey: ['broadcast'],
    queryFn:  async () => {
      const token = localStorage.getItem('polywally_token')
      const headers: Record<string, string> = {}
      if (token) headers['Authorization'] = `Bearer ${token}`
      const r = await fetch(`${import.meta.env.VITE_API_URL ?? 'https://api.polywally.com'}/api/market/broadcast`, { headers })
      if (!r.ok) return { active: null }
      return r.json()
    },
    refetchInterval: 300_000,
    staleTime: 60_000,
  })
  const broadcast = broadcastData?.active ?? null

  function handleLogout() {
    logout()
    navigate('/')
  }

  return (
    <div className="layout-root" style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside className="layout-sidebar" style={{
        width: 220, background: 'var(--paper)', borderRight: '1px solid var(--edge)',
        display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0,
        position: 'sticky', top: 0, height: '100vh'
      }}>
        {/* Logo */}
        <div className="layout-logo" style={{ padding: '0 20px 28px', borderBottom: '1px solid var(--edge)' }}>
          <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.2rem', color: 'var(--bill)', letterSpacing: '-0.02em' }}>
            Polywally<span style={{ fontFamily: 'Syne Mono', fontSize: '0.4rem', color: 'var(--cash)', verticalAlign: 'super', marginLeft: 2 }}>™</span>
          </div>
          <MarketClock />
        </div>

        {/* Nav */}
        <nav className="layout-nav" style={{ flex: 1, padding: '20px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 12px', borderRadius: 3,
              fontFamily: 'Syne', fontWeight: 600, fontSize: '0.82rem',
              textDecoration: 'none',
              color: isActive ? 'var(--cash)' : 'var(--muted)',
              background: isActive ? 'var(--stamp)' : 'transparent',
              transition: 'all 0.15s', position: 'relative'
            })}>
              <img src={NAV_ICONS[n.iconKey]} alt="" style={{ width: 20, height: 20, objectFit: 'contain', flexShrink: 0, imageRendering: 'auto' }} />
              <span className="nav-label-full">{n.label}</span>
              <span className="nav-label-short">{n.mobileLabel}</span>
              {n.to === '/intelligence' && unreadAlerts > 0 && (
                <span style={{
                  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                  background: '#e05c5c', color: '#fff',
                  fontFamily: 'Syne Mono', fontSize: '.5rem', fontWeight: 800,
                  borderRadius: '50%', width: 16, height: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  lineHeight: 1,
                }}>
                  {unreadAlerts > 9 ? '9+' : unreadAlerts}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="layout-user" style={{ padding: '16px 20px', borderTop: '1px solid var(--edge)' }}>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '0.62rem', color: 'var(--muted)', marginBottom: 4 }}>
            {user?.email}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            <button onClick={handleLogout} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'Syne Mono', fontSize: '0.6rem', color: 'var(--muted)'
            }}>
              logout
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="layout-main" style={{ flex: 1, minWidth: 0, overflow: 'auto', overflowX: 'hidden', padding: 0 }}>
        <NewsTicker compact />
        {broadcast && (
          <div style={{
            background: 'rgba(212,168,67,.08)', borderBottom: '1px solid rgba(212,168,67,.2)',
            padding: '8px 32px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' as const,
          }}>
            <img src="/icons/broadcast.svg" alt="" style={{width:14,height:14,flexShrink:0}}/>
            <span style={{ fontFamily: 'Syne Mono', fontSize: '.66rem', color: 'var(--amber)', flex: 1 }}>
              {broadcast.message}
            </span>
          </div>
        )}
        <div className="layout-page-content">
          {/* Keyed by path so navigating away from a crashed page clears the boundary */}
          <ErrorBoundary key={location.pathname}>
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  )
}
